import React, { useState, useEffect } from 'react';
import { 
  Home, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  FileText, 
  Calendar,
  MapPin,
  User,
  LogOut,
  Briefcase
} from 'lucide-react';
import ContractorApiService from '../../services/ContractorApiService';

const ContractorDashboard = ({ contractor, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [batchPaymentHistory, setBatchPaymentHistory] = useState([]);
  const [xeroChecks, setXeroChecks] = useState([]);
  const [paymentViewMode, setPaymentViewMode] = useState('checks'); // 'checks', 'batch', or 'individual'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Home },
    { id: 'workorders', name: 'My Work Orders', icon: Briefcase },
    { id: 'payments', name: 'Payment History', icon: DollarSign }
  ];

  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'workorders') {
      // Both tabs need dashboard data; only fetch if not already loaded
      if (!dashboardData) loadDashboard();
    } else if (activeTab === 'payments') {
      loadPaymentHistory();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ContractorApiService.getDashboard();
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading contractor dashboard:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Loading payment history...');
      const [payments, batchPayments, checks] = await Promise.all([
        ContractorApiService.getPaymentHistory(),
        ContractorApiService.getBatchPaymentHistory(),
        ContractorApiService.getXeroChecks()
      ]);
      console.log('✅ Payment history loaded:', { payments, batchPayments, checks });
      setPaymentHistory(payments);
      setBatchPaymentHistory(batchPayments);
      setXeroChecks(checks);
    } catch (error) {
      console.error('❌ Error loading payment history:', error);
      setError(`Failed to load payment history: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'Assigned': { color: 'bg-blue-100 text-blue-800', icon: User },
      'InProgress': { color: 'bg-purple-100 text-purple-800', icon: Clock },
      'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Finalized': { color: 'bg-emerald-100 text-emerald-800', icon: FileText },
      'Paid': { color: 'bg-gray-100 text-gray-700', icon: DollarSign }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <div className="text-2xl text-blue-600">🏠</div>
                  <div className="absolute -top-0.5 -right-0.5 text-xs text-green-500">🛡️</div>
                </div>
                <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  ACVentures
                </div>
                <span className="text-sm text-gray-500">Contractor Portal</span>
              </div>
              
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? 'border-blue-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                <div className="font-medium">{contractor.contractorName}</div>
                <div className="text-xs text-gray-500">{contractor.email}</div>
              </div>
              <button
                onClick={onLogout}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 px-4 sm:px-0">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => setError(null)}
                      className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'dashboard' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Welcome, {contractor.contractorName}</h1>
              <p className="text-gray-600 mt-1">Track your work orders and payments with ACVentures</p>
            </div>

            {/* Summary Cards */}
            {dashboardData?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Work Orders</p>
                      <p className="text-2xl font-bold text-blue-600">{dashboardData.summary.activeWorkOrders}</p>
                    </div>
                    <Briefcase className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Outstanding Payments</p>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(dashboardData.summary.outstandingAmount)}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-500" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">YTD Earnings</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboardData.summary.ytdEarnings)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Completed Jobs</p>
                      <p className="text-2xl font-bold text-purple-600">{dashboardData.summary.completedWorkOrders}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>
            )}

            {/* Recent Work Orders */}
            {dashboardData?.activeWorkOrders && (
              <div className="bg-white rounded-lg shadow border mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Work Orders</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">My Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardData.activeWorkOrders.slice(0, 5).map((workOrder) => (
                        <tr key={workOrder.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{workOrder.workOrderNumber}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="w-4 h-4 mr-1" />
                              {workOrder.location || 'TBD'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {workOrder.clientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(workOrder.myCost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(workOrder.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString() : 'TBD'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Outstanding Payments */}
            {dashboardData?.outstandingPayments?.length > 0 && (
              <div className="bg-white rounded-lg shadow border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Outstanding Payments</h3>
                  <p className="text-sm text-gray-600">Work completed, payment pending</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardData.outstandingPayments.map((payment) => (
                        <tr key={payment.workOrderId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                            {payment.workOrderNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {payment.clientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {payment.location}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Payment Pending
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'workorders' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My Work Orders</h2>
              <p className="text-gray-600">All work orders assigned to you</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading work orders...</span>
              </div>
            ) : dashboardData?.allWorkOrders?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.allWorkOrders.map((workOrder) => (
                  <div key={workOrder.id} className="bg-white rounded-lg shadow border p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{workOrder.workOrderNumber}</h3>
                        <p className="text-gray-600">{workOrder.clientName}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(workOrder.myDatePaid ? 'Paid' : workOrder.status)}
                        <p className="text-lg font-bold text-green-600 mt-1">{formatCurrency(workOrder.myCost)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Location</label>
                        <p className="text-gray-900 flex items-center">
                          <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                          {workOrder.location || 'TBD'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Due Date</label>
                        <p className="text-gray-900 flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString() : 'TBD'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Date Paid</label>
                        <p className={`flex items-center ${workOrder.myDatePaid ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                          <DollarSign className="w-4 h-4 mr-1" />
                          {workOrder.myDatePaid ? new Date(workOrder.myDatePaid).toLocaleDateString() : 'Unpaid'}
                        </p>
                      </div>
                    </div>

                    {workOrder.description && (
                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-500">Description</label>
                        <p className="text-gray-900 mt-1">{workOrder.description}</p>
                      </div>
                    )}

                    {workOrder.myDescription && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <label className="text-sm font-medium text-blue-700">My Task</label>
                        <p className="text-blue-900 mt-1">{workOrder.myDescription}</p>
                        {workOrder.myServiceType && (
                          <p className="text-xs text-blue-600 mt-1">Service: {workOrder.myServiceType}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No work orders found</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
                  <p className="text-gray-600">Your payment history with ACVentures</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPaymentViewMode('checks')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      paymentViewMode === 'checks'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📝 Xero Checks
                  </button>
                  <button
                    onClick={() => setPaymentViewMode('batch')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      paymentViewMode === 'batch'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    By Batch
                  </button>
                  <button
                    onClick={() => setPaymentViewMode('individual')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      paymentViewMode === 'individual'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Individual
                  </button>
                </div>
              </div>
            </div>

            {loading && activeTab === 'payments' ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading payment history...</span>
              </div>
            ) : paymentViewMode === 'checks' ? (
              // Xero Live Check Register
              <div className="space-y-6">
                {xeroChecks.length > 0 ? (
                  xeroChecks.map((check, idx) => (
                    <div key={idx} className="bg-white rounded-lg shadow border overflow-hidden">
                      {/* Check Header */}
                      <div className="bg-green-50 px-6 py-4 border-b border-green-200">
                        <div className="flex justify-between items-center flex-wrap gap-2">
                          <div>
                            {/* Check number — the most important thing to show */}
                            <h3 className="text-xl font-bold text-green-900 flex items-center gap-3">
                              📝
                              {check.checkReference
                                ? <span>Check <span className="font-mono">#{check.checkReference}</span></span>
                                : <span className="text-green-700">{check.isBatchPayment ? 'Batch Payment' : 'Payment'}</span>
                              }
                              {check.isBatchPayment && (
                                <span className="text-xs font-medium bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                                  Batch
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                              {new Date(check.paymentDate).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'long', day: 'numeric'
                              })}
                              {' '}•{' '}{check.billCount} bill{check.billCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-700">{formatCurrency(check.totalAmount)}</p>
                            <p className="text-xs text-green-500 mt-0.5">Total Paid</p>
                          </div>
                        </div>
                      </div>

                      {/* Bills covered by this check */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {check.bills.map((bill, bIdx) => (
                              <tr key={bIdx} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                                  {bill.billNumber || bill.billId.substring(0, 8) + '…'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                  {bill.workOrderNumber || <span className="text-gray-400 italic">—</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {bill.clientName || '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {bill.location || '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                                  {formatCurrency(bill.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No check payments found in Xero</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Paid bills will appear here once the org is connected to Xero
                    </p>
                  </div>
                )}
              </div>
            ) : paymentViewMode === 'batch' ? (
              // Batch Payment View
              <div className="space-y-6">
                {batchPaymentHistory.length > 0 ? (
                  batchPaymentHistory.map((batch, index) => (
                    <div key={index} className="bg-white rounded-lg shadow border overflow-hidden">
                      {/* Batch Header */}
                      <div className="bg-green-50 px-6 py-4 border-b border-green-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-lg font-semibold text-green-800 flex items-center gap-2">
                              {batch.paymentMethod === 'Check' ? '📝' : '🏦'} 
                              {batch.paymentMethod} Payment
                              {batch.paymentReference && ` - ${batch.paymentReference}`}
                            </h3>
                            <p className="text-sm text-green-600">
                              {new Date(batch.datePaid).toLocaleDateString()} • {batch.workOrderCount} work orders
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-700">{formatCurrency(batch.totalAmount)}</p>
                            <p className="text-sm text-green-600">Total Payment</p>
                          </div>
                        </div>
                      </div>

                      {/* Work Orders in this Batch */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {batch.workOrders.map((payment) => (
                              <tr key={payment.workOrderId} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                  {payment.workOrderNumber}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {payment.clientName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {payment.location}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                  {formatCurrency(payment.amountPaid)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                  {payment.xeroBillNumber || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No batch payments found</p>
                    <p className="text-sm text-gray-400 mt-2">Payments will appear here once bills are paid in batches</p>
                  </div>
                )}
              </div>
            ) : (
              // Individual Payment View
              <div>
                {paymentHistory.length > 0 ? (
                  <div className="bg-white rounded-lg shadow border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentHistory.map((payment) => (
                          <tr key={payment.workOrderId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.datePaid ? new Date(payment.datePaid).toLocaleDateString() : 'Pending'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                              {payment.workOrderNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.clientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {payment.location}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(payment.amountPaid || payment.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentMethod || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                              {payment.paymentReference || payment.xeroBillNumber || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No payment history available yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ContractorDashboard;

